export interface RestrictionData {
	type: RestrictionType;
	severity: RestrictionSeverity;
	appliesToAllRoomTypes: boolean;
	hasRecommendedChange: boolean;
	details?: string;
}

export enum RestrictionType {
	NONE = 'none',
	CLOSED = 'closed',
	SOFT_CLOSE = 'soft_close',
	MINMAX = 'minmax',
	MLOS = 'mlos',
}

export enum RestrictionSeverity {
	NONE = 'none',
	MODERATE = 'moderate',
	STRONG = 'strong',
}

const getMlosConfig = (nights: number) => {
	const isHighSeverity = nights >= 5; // 5+ nights are high severity (red)

	return {
		icon: isHighSeverity ? '🟥' : '🟨',
		color: isHighSeverity ? '#ef4444' : '#eab308', // red-500 or yellow-500
		bgColor: isHighSeverity ? '#fee2e2' : '#fef08a', // red-100 or yellow-200
		severity: isHighSeverity
			? RestrictionSeverity.STRONG
			: RestrictionSeverity.MODERATE,
		label: `Minimum ${nights} Night Stay`,
	};
};

export const RESTRICTION_CONFIG = {
	[RestrictionType.NONE]: {
		icon: '🟩',
		color: '#22c55e', // green-500
		bgColor: '#dcfce7', // green-100
		severity: RestrictionSeverity.NONE,
		label: 'No Restriction',
	},
	[RestrictionType.CLOSED]: {
		icon: '🟥',
		color: '#ef4444', // red-500
		bgColor: '#fee2e2', // red-100
		severity: RestrictionSeverity.STRONG,
		label: 'Closed',
	},
	[RestrictionType.SOFT_CLOSE]: {
		icon: '🟧',
		color: '#f97316', // orange-500
		bgColor: '#fed7aa', // orange-200
		severity: RestrictionSeverity.MODERATE,
		label: 'Soft Close',
	},
	[RestrictionType.MINMAX]: {
		icon: '🟧',
		color: '#f97316', // orange-500
		bgColor: '#fed7aa', // orange-200
		severity: RestrictionSeverity.MODERATE,
		label: 'MinMax',
	},
	[RestrictionType.MLOS]: {
		icon: '🟨', // Default - will be overridden by getMlosConfig
		color: '#eab308', // Default - will be overridden by getMlosConfig
		bgColor: '#fef08a', // Default - will be overridden by getMlosConfig
		severity: RestrictionSeverity.MODERATE, // Default - will be overridden by getMlosConfig
		label: 'Minimum Length of Stay', // Default - will be overridden by getMlosConfig
	},
} as const;

export const getRestrictionConfig = (
	restrictionType: RestrictionType,
	nights?: number,
) => {
	if (restrictionType === RestrictionType.MLOS && nights) {
		return getMlosConfig(nights);
	}
	return (
		RESTRICTION_CONFIG[restrictionType] ||
		RESTRICTION_CONFIG[RestrictionType.NONE]
	);
};

const RESTRICTION_CODE_MAP: Record<string, RestrictionType> = {
	// No restriction codes
	none: RestrictionType.NONE,
	open: RestrictionType.NONE,
	// Closed restriction codes
	closed: RestrictionType.CLOSED,
	close: RestrictionType.CLOSED,
	cta: RestrictionType.CLOSED,
	ctd: RestrictionType.CLOSED,
	// Soft close restriction codes
	soft_close: RestrictionType.SOFT_CLOSE,
	// MinMax restriction codes
	minmax: RestrictionType.MINMAX,
	// MLOS restriction codes - handled dynamically by parseMlosRestriction
};

interface RestrictionParseResult {
	type: RestrictionType;
	nights?: number;
}

const parseMlosRestriction = (code: string): RestrictionParseResult | null => {
	// Handle MLOS codes: mlos2, mlos3, rates.restrictionlos4, mlos-5.00, MLOS-6, etc.
	const mlosPatterns = [
		/^mlos(\d+)$/i, // mlos2, mlos3
		/^rates\.restrictionlos(\d+)$/i, // rates.restrictionlos2
		/^mlos-(\d+)(?:\.00)?$/i, // mlos-2, mlos-2.00, MLOS-3
	];

	for (const pattern of mlosPatterns) {
		const match = code.match(pattern);
		if (match) {
			const nights = Number.parseInt(match[1]);
			return { type: RestrictionType.MLOS, nights };
		}
	}

	return null;
};

const parseRestrictionWithLOS = (
	code: string,
): RestrictionParseResult | null => {
	// Handle MLOS codes first
	const mlosResult = parseMlosRestriction(code);
	if (mlosResult) return mlosResult;

	// Handle Soft Close with LOS units (e.g., "soft_close-2", "soft_close-3")
	if (code.startsWith('soft_close-')) {
		return { type: RestrictionType.SOFT_CLOSE };
	}

	// Handle MinMax with LOS units (e.g., "minmax-2", "minmax-3")
	if (code.startsWith('minmax-')) {
		return { type: RestrictionType.MINMAX };
	}

	return null;
};

export const parseRestrictionCode = (
	code?: string | any,
): RestrictionParseResult => {
	// Handle non-string values
	if (!code || typeof code !== 'string') return { type: RestrictionType.NONE };

	// Trim whitespace and convert to lowercase
	const normalizedCode = code.trim().toLowerCase();

	// First try exact match
	const restrictionType = RESTRICTION_CODE_MAP[normalizedCode];
	if (restrictionType) {
		return { type: restrictionType };
	}

	// Then try parsing with LOS units
	const losRestrictionType = parseRestrictionWithLOS(normalizedCode);
	if (losRestrictionType) {
		return losRestrictionType;
	}

	console.error(`Invalid restriction code: ${code}`);
	return { type: RestrictionType.NONE };
};

export const getRestrictionFromCode = (
	code?: string | any,
): RestrictionType => {
	const result = parseRestrictionCode(code);
	return result.type;
};
